import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Hash,
  Loader2,
  AlertOctagon,
  Plus,
  FileText,
  Boxes,
} from "lucide-react";
import Pill from "@/components/Pill";
import AddInlineDialog from "@/components/AddInlineDialog";
import { useRefData } from "@/context/RefDataContext";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const ref = useRefData();
  const [doc, setDoc] = useState(null);
  const [related, setRelated] = useState({ tickets: [] });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const refreshRelated = useCallback(async () => {
    try {
      const { data } = await api.get(`/assets/${id}/related`);
      setRelated(data);
    } catch (_) {}
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get(`/assets/${id}`),
          api.get(`/assets/${id}/related`),
        ]);
        setDoc(a.data);
        setRelated(b.data);
      } catch (e) {
        toast.error("Asset not found");
        navigate("/assets");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-sm" data-testid="asset-detail-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading asset…
      </div>
    );
  }
  if (!doc) return null;

  const projectLabel = ref.projects?.find((p) => p.value === doc.project_id)?.label;
  const files = Array.isArray(doc.files) ? doc.files : [];

  const ticketCfg = {
    endpoint: "/issues",
    title: "New Ticket",
    fields: [
      { key: "subject", label: "Subject", required: true, full: true },
      { key: "project_id", label: "CDARK", type: "select", optionsRef: "projects" },
      { key: "asset_id", label: "Related Asset", type: "select", optionsRef: "assets" },
      { key: "team_id", label: "Team", type: "select", optionsRef: "teams" },
      { key: "priority", label: "Priority", type: "select", optionsRef: "priorities" },
      { key: "status", label: "Status", type: "select", optionsRef: "statuses" },
      { key: "description", label: "Ticket Summary", type: "textarea", full: true },
    ],
    initialForm: {
      subject: "",
      project_id: doc.project_id || "",
      asset_id: id,
      team_id: "",
      priority: "Medium",
      status: "Open",
      description: "",
    },
    locked: ["asset_id"],
  };

  return (
    <div className="space-y-8" data-testid="asset-detail-page">
      <div>
        <Link
          to="/assets"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="asset-detail-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Assets
        </Link>
      </div>

      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-overline mb-2 flex items-center gap-2">
            <Hash className="w-3 h-3" />
            <span className="font-mono normal-case tracking-normal" data-testid="asset-detail-code">
              {doc.code}
            </span>
          </div>
          <h1
            className="text-3xl font-semibold tracking-tight text-white"
            style={{ fontFamily: "Outfit" }}
            data-testid="asset-detail-type"
          >
            <Boxes className="inline w-6 h-6 mr-2 mb-1 text-zinc-400" />
            {doc.asset_type || "—"}
          </h1>
          <p className="text-sm text-zinc-400 mt-2 flex items-center gap-x-3 gap-y-1.5 flex-wrap">
            <span className="text-zinc-500 text-xs">
              OEM: <span className="text-zinc-300">{doc.oem_name || "—"}</span>
            </span>
            <span className="text-zinc-500 text-xs">
              Model: <span className="text-zinc-300 font-mono">{doc.model_number || "—"}</span>
            </span>
            {projectLabel && (
              <Link
                to={`/projects/${doc.project_id}`}
                className="text-xs text-sky-400 hover:underline"
              >
                CDARK: {projectLabel}
              </Link>
            )}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => navigate("/assets")}
          className="bg-zinc-900 border border-white/10 text-white hover:bg-zinc-800"
        >
          Back to list
        </Button>
      </header>

      {/* Specs */}
      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
        <div className="label-overline mb-3">Specifications</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
          <Row label="OEM Name" value={doc.oem_name} />
          <Row label="Model Number" value={doc.model_number} mono />
          <Row label="Serial Number" value={doc.serial_number} mono />
          <Row label="Asset Type" value={doc.asset_type} />
          <Row label="Purchase Date" value={doc.purchase_date} />
          <Row label="Purchase Invoice #" value={doc.purchase_invoice_number} mono />
          <Row label="Vendor Relationship #" value={doc.vendor_relationship_number} mono />
          <Row label="License Expiry" value={doc.license_expiry_date} />
          <Row label="Commissioning Date" value={doc.commissioning_date} />
          <Row label="Delivery Challan #" value={doc.delivery_challan_number} mono />
        </div>
        {doc.description && (
          <div className="mt-4 border-t border-white/5 pt-4">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Description</div>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap">{doc.description}</p>
          </div>
        )}
      </section>

      {/* Related Tickets */}
      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5" data-testid="asset-related-tickets">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 label-overline">
            <AlertOctagon className="w-3.5 h-3.5" strokeWidth={1.75} /> Related tickets
          </div>
          <div className="flex items-center gap-3">
            <Link to="/issues" className="text-xs text-zinc-500 hover:text-white">
              View all →
            </Link>
            {can(user, "issues", "create") && (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1 transition-colors"
                data-testid="asset-add-ticket-btn"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            )}
          </div>
        </div>
        {!related.tickets || related.tickets.length === 0 ? (
          <div className="text-xs text-zinc-600 py-6 text-center">No tickets raised for this asset.</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {related.tickets.slice(0, 30).map((t) => (
              <li
                key={t.id}
                className="py-2.5 flex items-center justify-between gap-3"
                data-testid={`asset-related-ticket-${t.id}`}
              >
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{t.subject}</div>
                  <div className="text-xs text-zinc-500 font-mono">{t.code}</div>
                </div>
                <div className="flex gap-1.5 items-center shrink-0">
                  <Pill kind="priority" value={t.priority} />
                  <Pill kind="status" value={t.status} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Files */}
      {files.length > 0 && (
        <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
          <div className="label-overline mb-3">Attachments</div>
          <div className="flex flex-wrap gap-2">
            {files.map((f) => (
              <a
                key={f.id}
                href={`${api.defaults.baseURL}/files/${f.id}/download?auth=${encodeURIComponent(localStorage.getItem("ops_token") || "")}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-sky-400 hover:underline bg-zinc-950 border border-white/10 rounded-md px-2 py-1"
              >
                <FileText className="w-3.5 h-3.5" />
                <span className="truncate max-w-[180px]">{f.filename}</span>
              </a>
            ))}
          </div>
        </section>
      )}

      <footer className="text-xs text-zinc-600 pt-4 border-t border-white/5">
        Created {doc.created_at ? new Date(doc.created_at).toLocaleString() : "—"}
      </footer>

      <AddInlineDialog
        open={adding}
        onOpenChange={(o) => !o && setAdding(false)}
        endpoint={ticketCfg.endpoint}
        title={ticketCfg.title}
        fields={ticketCfg.fields}
        initialForm={ticketCfg.initialForm}
        lockedKeys={ticketCfg.locked}
        onCreated={() => {
          setAdding(false);
          refreshRelated();
          ref.refresh?.();
        }}
      />
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-0.5">{label}</div>
      <div className={`text-sm ${value ? "text-white" : "text-zinc-600"} ${mono ? "font-mono" : ""}`}>
        {value || "—"}
      </div>
    </div>
  );
}
